import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapTrifold, ArrowsClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UsStateMap } from "@/components/demand/UsStateMap";
import { DemandGrid } from "@/components/demand/DemandGrid";
import { DemandTable, money } from "@/components/demand/DemandTable";
import { DemandEmailControls } from "@/components/demand/DemandEmailControls";
import { useNavigate } from "react-router-dom";

const RANGES = [["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["custom", "Custom"]];
const VERTICALS = ["auto", "truck", "rideshare", "motorcycle"];
const seg = (on) => `px-3 py-1.5 text-[10px] uppercase tracking-widest border-r-2 border-black last:border-r-0 transition-colors duration-150 ${on ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`;

function prefillFor(r) {
  const funnel = r.funnel === "injury" && VERTICALS.includes(r.case_type) ? r.case_type : r.funnel;
  return { states: [r.state], funnels: [funnel] };
}

function Kpi({ label, value, testId, hot }) {
  return (
    <div className={`border-2 border-black p-3 ${hot ? "bg-black text-white" : "bg-white"}`} data-testid={testId}>
      <div className={`label-mono ${hot ? "text-zinc-400" : ""}`}>{label}</div>
      <div className="font-chivo font-black text-xl sm:text-2xl mt-1 break-words leading-tight">{value}</div>
    </div>
  );
}

function RangePicker({ range, setRange, custom, setCustom }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex border-2 border-black" data-testid="demand-range">
        {RANGES.map(([k, l]) => (
          <button key={k} type="button" className={seg(range === k)} onClick={() => setRange(k)} data-testid={`demand-range-${k}`}>{l}</button>
        ))}
      </div>
      {range === "custom" && ["from", "to"].map((k) => (
        <input key={k} type="date" value={custom[k]} onChange={(e) => setCustom((c) => ({ ...c, [k]: e.target.value }))}
               className="border-2 border-black px-2 py-1 text-xs" data-testid={`demand-range-${k}`} />
      ))}
    </div>
  );
}

function useDemand(range, custom) {
  const [state, setState] = useState({ data: null, error: "", loading: true });
  const load = useCallback(async () => {
    if (range === "custom" && !(custom.from && custom.to)) return;
    setState((s) => ({ ...s, loading: true }));
    const params = range === "custom" ? { date_from: custom.from, date_to: custom.to } : { days: Number(range) };
    try {
      setState({ data: (await api.get("/admin/demand-map", { params })).data, error: "", loading: false });
    } catch (e) {
      const d = e.response?.data?.detail;
      setState({ data: null, error: typeof d === "string" ? d : "Could not load the demand map", loading: false });
    }
  }, [range, custom]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function DemandBody({ data, priceFor, onAddBuyer, canAdd }) {
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("map");
  const [cols, setCols] = useState("funnel");
  const counts = useMemo(() => Object.fromEntries(data.states.map((s) => [s.state, s.unmatched])), [data]);
  const rows = selected ? data.rows.filter((r) => r.state === selected) : data.rows;
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex border-2 border-black">
          <button type="button" className={seg(view === "map")} onClick={() => setView("map")} data-testid="demand-view-map">US map</button>
          <button type="button" className={seg(view === "grid")} onClick={() => setView("grid")} data-testid="demand-view-grid">State grid</button>
        </div>
        {view === "grid" && (
          <div className="flex border-2 border-black">
            <button type="button" className={seg(cols === "funnel")} onClick={() => setCols("funnel")} data-testid="demand-grid-cols-funnel">By funnel</button>
            <button type="button" className={seg(cols === "case")} onClick={() => setCols("case")} data-testid="demand-grid-cols-case">By case type</button>
          </div>
        )}
        {selected && (
          <button type="button" onClick={() => setSelected(null)} className="border-2 border-black bg-red-600 text-white px-2 py-1 text-[10px] uppercase tracking-widest" data-testid="demand-clear-state">
            {selected} · clear
          </button>
        )}
      </div>
      {view === "map" ? <UsStateMap counts={counts} selected={selected} onSelect={setSelected} />
        : <DemandGrid data={data} mode={cols} selected={selected} onSelect={setSelected} />}
      <div className="label-mono">{selected ? `Unmatched areas in ${selected}` : "All unmatched areas"} · click a state to filter</div>
      <DemandTable rows={rows} priceFor={priceFor} onAddBuyer={onAddBuyer} canAdd={canAdd} />
    </>
  );
}

export const DemandMapPanel = () => {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin"].includes(user?.role);
  const [range, setRange] = useState("30");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [override, setOverride] = useState("");
  const navigate = useNavigate();
  const { data, error, loading, reload } = useDemand(range, custom);
  const price = Number(override) > 0 ? Number(override) : null;
  const priceFor = (r) => price ?? r.price;
  const missed = data ? data.rows.reduce((t, r) => t + r.unmatched * priceFor(r), 0) : 0;
  const top = data?.totals.top_area;

  return (
    <div className="p-4 flex flex-col gap-4" data-testid="demand-map-panel">
      <div className="flex items-start gap-3 flex-wrap">
        <MapTrifold size={22} weight="bold" />
        <div className="flex-1 min-w-[220px]">
          <div className="font-chivo font-black uppercase tracking-tighter text-lg leading-none">Funnel demand map</div>
          <div className="text-[11px] text-zinc-600 mt-1">Shared-funnel leads no buyer took (sent to the marketplace or left undelivered). Test leads are not counted.</div>
        </div>
        <RangePicker range={range} setRange={setRange} custom={custom} setCustom={setCustom} />
        <button type="button" onClick={reload} className="border-2 border-black p-1.5 hover:bg-black hover:text-white" title="Refresh" data-testid="demand-refresh">
          <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
        </button>
      </div>
      {error && <div className="border-2 border-red-700 bg-red-50 p-3 text-xs" data-testid="demand-error">{error}</div>}
      {range === "custom" && !(custom.from && custom.to) && <div className="label-mono">Pick a start and end date.</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Unmatched leads" value={data.totals.unmatched} testId="demand-kpi-unmatched" hot />
            <Kpi label="States" value={data.totals.states} testId="demand-kpi-states" />
            <Kpi label="Est. missed revenue" value={money(missed)} testId="demand-kpi-missed" />
            <Kpi label="Top area" value={top ? `${top.state} · ${top.case_label}` : "—"} testId="demand-kpi-top" />
          </div>
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <span className="label-mono">Price per lead for estimates</span>
            <input type="number" min="1" value={override} onChange={(e) => setOverride(e.target.value)} placeholder={`${data.avg_price}`}
                   className="border-2 border-black px-2 py-1 w-24 text-xs" data-testid="demand-price-override" />
            <span className="text-zinc-600" data-testid="demand-price-basis">
              {price ? "Using your price." : data.price_basis === "buyers"
                ? `Using your buyers' average price per state (overall $${data.avg_price}).`
                : `No live buyer prices yet — using the marketplace default $${data.avg_price}.`}
            </span>
          </div>
          <DemandBody key={`${data.range.from}-${data.range.to}`} data={data} priceFor={priceFor} canAdd={canEdit}
                      onAddBuyer={(r) => navigate("/clients/setup", { state: { prefill: prefillFor(r) } })} />
        </>
      )}
      <DemandEmailControls canEdit={canEdit} />
    </div>
  );
};
